import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import {
  FaCaretLeft,
  FaClipboardList,
  FaCrown,
  FaDiscord,
  FaLink,
  FaUser,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { GET_TIPPER_PROFILE } from "../../queries/mutation/broadcaster/queries";
import { GET_TIP_QUEUE, SET_TIP_QUEUE_STATUS } from "../../queries/tipQueue";
import { Tipper } from "../../types/Tipper";
import { TipperProfile } from "../../types/TipperProfile";
import { TipQueue, TipQueueEntry, TipQueueStatus } from "../../types/TipQueue";
import CrimcoinAdjuster from "../CrimcoinAdjuster";
import LoadingPage from "../LoadingPage";
import { EntryCard, Handlers } from "../tipQueue/EntryCard";
import {
  formatDate,
  timeAgo,
  tokensToDollars,
} from "./broadcasters/community/format";
import StatTile from "./broadcasters/community/StatTile";

type Props = {
  tipper: Tipper;
  clearTipper: () => void;
  refreshTipper: () => void;
};

// One titled block on the profile.
const Section = ({
  title,
  icon,
  aside,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="rounded-box bg-base-200 p-3 flex flex-col gap-2">
    <div className="flex items-center justify-between gap-2">
      <h3 className="font-semibold flex items-center gap-2">
        {icon}
        {title}
      </h3>
      {aside}
    </div>
    {children}
  </section>
);

// A CB tipper's profile, as the broadcaster sees them. Who they are up top,
// the numbers, then what's owed, what they tipped, and their accounts.
const TipperDetail = (props: Props) => {
  const { tipper, clearTipper, refreshTipper } = props;
  const { loading, data, error, refetch } = useQuery(GET_TIPPER_PROFILE, {
    variables: { cbUsername: tipper.username },
    fetchPolicy: "cache-and-network",
  });

  // Their banked tips, straight from the queue.
  const { data: queueData, refetch: refetchQueue } = useQuery(GET_TIP_QUEUE, {
    fetchPolicy: "cache-and-network",
    pollInterval: 10000,
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [setStatus] = useMutation(SET_TIP_QUEUE_STATUS, {
    onError: (e) => toast.error(e.message),
  });
  const queue: TipQueue | undefined = queueData?.getTipQueue;
  const banked: TipQueueEntry[] = (queue?.banked ?? []).filter(
    (e) => e.username === tipper.username,
  );
  const queueHandlers: Handlers = {
    busyId,
    onStatus: async (entry, status: TipQueueStatus) => {
      setBusyId(entry.id);
      try {
        const { data } = await setStatus({ variables: { id: entry.id, status } });
        if (data) await refetchQueue();
      } finally {
        setBusyId(null);
      }
    },
    onMove: () => {},
  };

  const profile: TipperProfile | null | undefined = data?.getTipperProfile;

  const back = (
    <button
      className="btn btn-ghost btn-sm btn-square"
      onClick={clearTipper}
      aria-label="Back to list"
      title="Back to list"
    >
      <FaCaretLeft size={24} />
    </button>
  );

  if (loading && !profile) {
    return <LoadingPage />;
  }
  // Reached by address, so the name may be one we have no record of.
  if (error || !profile) {
    return (
      <div className="bg-base-200 h-full flex flex-col items-center p-2 sm:p-4">
        <div className="card bg-base-100 w-full max-w-3xl">
          <div className="card-body p-4 gap-2">
            <div className="flex items-center gap-2">
              {back}
              <span>
                No CB tipper record for{" "}
                <span className="text-warning">{tipper.username}</span>
              </span>
            </div>
            {error && (
              <div className="text-xs text-base-content/60">{error.message}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const maxCount = Math.max(1, ...profile.tipCounts.map((c) => c.count));

  return (
    <div className="bg-base-200 h-full flex flex-col items-center p-2 sm:p-4 overflow-y-auto">
      <div className="card bg-base-100 w-full max-w-3xl shadow">
        <div className="card-body p-3 sm:p-5 gap-4">
          {/* Who */}
          <header className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {back}
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-base-content/60">
                  CB tipper
                </div>
                <h2 className="text-2xl font-bold text-warning break-all leading-tight">
                  {profile.cbUsername}
                </h2>
              </div>
              <FaUser size={22} className="ml-auto text-base-content/40 shrink-0" />
            </div>
            <div className="flex flex-wrap gap-1">
              {profile.inFanclub && (
                <span className="badge badge-success badge-outline">fan club</span>
              )}
              {profile.isVip && (
                <span className="badge badge-success gap-1">
                  <FaCrown /> VIP
                </span>
              )}
              {profile.grantUntil && (
                <span className="badge badge-accent badge-outline">
                  VIP until {formatDate(profile.grantUntil)}
                </span>
              )}
              {profile.reinUsername ? (
                <span className="badge badge-info badge-outline">
                  Reinstorm: {profile.reinUsername}
                </span>
              ) : (
                <span className="badge badge-ghost">no Reinstorm account</span>
              )}
              {profile.discordName ? (
                <span
                  className={`badge gap-1 ${
                    profile.discordLinkSource === "reinstorm"
                      ? "badge-success badge-outline"
                      : "badge-info badge-outline"
                  }`}
                  title={
                    profile.discordLinkSource === "reinstorm"
                      ? "Linked through their own Reinstorm account"
                      : "Linked by a mod"
                  }
                >
                  <FaDiscord /> {profile.discordName}
                </span>
              ) : (
                <span className="badge badge-ghost gap-1">
                  <FaDiscord /> no Discord
                </span>
              )}
            </div>
            <div className="text-sm text-base-content/60 flex flex-wrap gap-x-4 gap-y-1">
              <span>Last seen {profile.lastSeen ? timeAgo(profile.lastSeen) : "never"}</span>
              {profile.firstJoinedFanclub && (
                <span>Fan club since {formatDate(profile.firstJoinedFanclub)}</span>
              )}
              {!profile.inFanclub && profile.lastFanclubEnded && (
                <span>Left fan club {formatDate(profile.lastFanclubEnded)}</span>
              )}
            </div>
          </header>

          {/* The numbers */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            <StatTile
              label="Lifetime tips"
              value={profile.lifetimeTips}
              hint={tokensToDollars(profile.lifetimeTips)}
              className="text-primary"
            />
            <StatTile
              label="Biggest tip"
              value={profile.lifetimeBiggestTip}
              hint={tokensToDollars(profile.lifetimeBiggestTip)}
              className="text-primary"
            />
            <StatTile label="Times tipped" value={profile.numberOfTips} />
            <StatTile label="Chips" value={profile.chips} className="text-warning" />
            <StatTile
              label="Crimcoins"
              value={profile.crimcoins ?? "—"}
              hint={profile.reinUsername ? undefined : "needs Reinstorm"}
              className="text-secondary"
            />
          </div>

          {/* What's owed */}
          <Section
            title={`Banked tips (${banked.length})`}
            icon={<FaClipboardList className="text-accent" />}
            aside={
              <Link to="/tip-queue" className="link link-accent text-xs">
                Open queue
              </Link>
            }
          >
            {banked.length === 0 ? (
              <div className="text-sm text-base-content/60">Nothing banked.</div>
            ) : (
              <ul className="flex flex-col gap-2 max-h-80 overflow-y-auto pr-1">
                {banked.map((entry, index) => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    lane="banked"
                    index={index}
                    count={banked.length}
                    handlers={queueHandlers}
                    canReorder={false}
                  />
                ))}
              </ul>
            )}
          </Section>

          {/* What they tipped */}
          <Section title="Recent tips">
            {profile.recentTips.length === 0 ? (
              <div className="text-sm text-base-content/60">No tips recorded here yet.</div>
            ) : (
              <ul className="flex flex-col divide-y divide-base-300 max-h-80 overflow-y-auto">
                {profile.recentTips.map((t) => (
                  <li key={t.id} className="py-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="font-semibold text-primary w-16 shrink-0">
                      {t.tokens.toLocaleString()}
                    </span>
                    <span className={t.itemName ? "text-success" : "text-base-content/50"}>
                      {t.itemName ?? "no menu item"}
                    </span>
                    <span className="ml-auto text-xs text-base-content/50 whitespace-nowrap">
                      {timeAgo(t.createdAt)}
                    </span>
                    {t.message && (
                      <span className="basis-full text-sm text-base-content/70 break-words">
                        “{t.message}”
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Tip breakdown" aside={<span className="text-xs text-base-content/60">by amount</span>}>
            {profile.tipCounts.length === 0 ? (
              <div className="text-sm text-base-content/60">Nothing to show yet.</div>
            ) : (
              <ul className="flex flex-col gap-1 max-h-72 overflow-y-auto pr-1">
                {profile.tipCounts.map((c) => (
                  <li key={c.amount} className="flex items-center gap-2 text-sm">
                    <span className="w-16 shrink-0 font-semibold text-primary text-right">
                      {c.amount.toLocaleString()}
                    </span>
                    <span className="flex-1 h-3 bg-base-300 rounded overflow-hidden">
                      <span
                        className="block h-full bg-primary/70"
                        style={{ width: `${Math.max(4, (c.count / maxCount) * 100)}%` }}
                      />
                    </span>
                    <span className="w-10 shrink-0 text-right">{c.count}×</span>
                    <span className="hidden sm:inline w-28 truncate text-xs text-base-content/60">
                      {c.itemName ?? ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Accounts */}
          <Section title="Accounts" icon={<FaLink className="text-base-content/60" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="rounded-box bg-base-100 p-3 flex flex-col gap-1">
                <div className="font-semibold">Reinstorm</div>
                {profile.reinUsername ? (
                  <div>
                    {profile.reinUsername}
                    <span className="text-base-content/60">
                      {" "}· {(profile.crimcoins ?? 0).toLocaleString()} crimcoins
                    </span>
                  </div>
                ) : (
                  <div className="text-base-content/60">
                    Not signed up, so no crimcoins or Discord through Reinstorm.
                  </div>
                )}
              </div>
              <div className="rounded-box bg-base-100 p-3 flex flex-col gap-1">
                <div className="font-semibold flex items-center gap-1">
                  <FaDiscord /> Discord
                </div>
                {profile.discordName ? (
                  <div>
                    @{profile.discordName}
                    <span className="text-base-content/60">
                      {" "}·{" "}
                      {profile.discordLinkSource === "reinstorm"
                        ? "linked by them on Reinstorm"
                        : "linked by a mod"}
                    </span>
                  </div>
                ) : (
                  <div className="text-base-content/60">
                    None linked.{" "}
                    <Link to="/vip-members" className="link link-info">
                      Link one on VIP Members
                    </Link>
                  </div>
                )}
              </div>
            </div>
            {profile.reinUserId && (
              <CrimcoinAdjuster
                userId={profile.reinUserId}
                username={profile.reinUsername ?? profile.cbUsername}
                balance={profile.crimcoins ?? 0}
                onAdjusted={() => {
                  refetch();
                  refreshTipper();
                }}
              />
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default TipperDetail;
